import { Component, ChangeDetectionStrategy, signal, computed, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IrisDmsFilesSandbox } from '@iris/common/modules/dms/sandbox/dms-files.sandbox';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IrisFilesService } from '../../../services/files.service';
import { TranslatePipe } from '@ngx-translate/core';
import { StrakitButtonComponent, StrakitTextComponent } from '@ngx-strakit/cdk/controls';
import { IrisLoaderModule } from '@iris/common/modules/loader';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';

export interface IrisDmsFileRenameResultI {
  name: string;
  rename: boolean;
  replace: boolean;
}
@Component({
  selector: 'iris-dms-file-rename-modal',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    ConfirmByKeypressDirective,

    IrisLoaderModule,

    SkModalContentComponent,
    StrakitTextComponent,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
  templateUrl: './file-rename-modal.component.html',
  styleUrls: ['./file-rename-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisDmsFileRenameModalComponent extends SkModalBase<IrisDmsFileRenameModalComponent, IrisDmsFileRenameResultI> implements OnInit {
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly filesService = inject(IrisFilesService);
  private readonly formBuilder = inject(FormBuilder);
  readonly data = inject<{
    fileName: string;
    folderId: string;
    fromForm: boolean;
    isFileAttached: boolean;
  }>(MAT_DIALOG_DATA);

  readonly originalFileName = signal<string>('');

  readonly fileNameWithoutExtension = computed(() => this.filesService.getFileNameWithoutExtension(this.originalFileName()));
  readonly extension = computed(() => this.filesService.getExtensionFromFileName(this.originalFileName()));

  readonly renameErrorLabel = computed(() => {
    switch(true) {
      case !this.fromForm():
        return 'label.dms.FileWithThisNameExists';
      case !this.isFileAttached():
        return 'label.df.RenameOrReplaceFileDialog';
      default:
        return 'label.df.RenameFileDialog';
    }
  });

  readonly duplicateChecking = signal(false);

  folderId: string;
  readonly isFileAttached = signal(false);
  readonly fromForm = signal(false);

  readonly editFileForm: FormGroup = this.formBuilder.group({
    name: new FormControl<string>('', [Validators.required]),
  });

  get name(): AbstractControl<string> {
    return this.editFileForm.get('name');
  }

  ngOnInit(): void {
    this.folderId = this.data.folderId ?? '';
    this.fromForm.set(this.data.fromForm ?? false);
    this.isFileAttached.set(this.data.isFileAttached ?? false);

    this.name.valueChanges
      .pipe(
        debounceTime(1000),
        filter(value => !!value && value !== this.fileNameWithoutExtension()),
        distinctUntilChanged(),
        switchMap(() => this.checkIfFileNameExist()),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe();

    this.processFileName(this.data.fileName);
  }

  private checkIfFileNameExist(): Observable<boolean> {
    this.duplicateChecking.set(true);
    return this.dmsFilesSandbox.fileNamesExist([this.fullFileName], this.folderId).pipe(
      tap(exists => {
        if (exists) {
          this.name.setErrors({ 'exists': true });
          this.name.markAsTouched();
        } else {
          this.name.setErrors(null);
        }
      }),
      finalize(() => this.duplicateChecking.set(false)),
    );
  }

  renameFile(rename: boolean, replace: boolean): void {
    this.closeWithResult({
      rename,
      replace,
      name: this.fullFileName,
    });
  }

  private get fullFileName(): string {
    return this.fromForm() ? this.name.value : `${this.name.value}.${this.extension()}`;
  }

  private processFileName(fileName: string): void {
    this.originalFileName.set(fileName);

    this.editFileForm.patchValue({
      name: this.fromForm() ? fileName : this.filesService.getFileNameWithoutExtension(fileName),
    });
  }
}
